import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ApiError } from "../api.js";
import { useOptionalAuth } from "../auth.js";
import {
  MetronomeProvider as BaseMetronomeProvider,
  isPracticePath,
  savePendingMetronomeInterval,
  useMetronome,
  type PendingMetronomeSave,
} from "./MetronomeBase.js";

export * from "./MetronomeBase.js";

const PENDING_METRONOME_STORAGE_PREFIX = "wmf.metronome.pending.v1.";

function pendingMetronomeStorageKey(accountId: string, studentId: string): string {
  return `${PENDING_METRONOME_STORAGE_PREFIX}${encodeURIComponent(accountId)}.${encodeURIComponent(studentId)}`;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isRecoveredPendingSave(
  value: unknown,
  studentId: string,
): value is PendingMetronomeSave {
  if (!value || typeof value !== "object") return false;
  const pending = value as Record<string, unknown>;
  const session = pending.session;
  if (!session || typeof session !== "object") return false;
  const parsedSession = session as Record<string, unknown>;

  if (pending.practicePath !== `/me/students/${studentId}/practice`) return false;
  if (!isFiniteNumber(parsedSession.startedAtMs)) return false;
  if (!isFiniteNumber(parsedSession.startedAtPerf)) return false;
  if (!isFiniteNumber(parsedSession.startBpm)) return false;
  if (typeof parsedSession.signature !== "string" || parsedSession.signature.length === 0) {
    return false;
  }
  if (!isFiniteNumber(pending.stoppedAtMs)) return false;
  if (pending.stoppedAtMs < parsedSession.startedAtMs) return false;
  if (!isFiniteNumber(pending.endBpm)) return false;
  if (
    pending.assignmentTitle !== undefined &&
    typeof pending.assignmentTitle !== "string"
  ) {
    return false;
  }

  if (pending.assigned !== undefined) {
    if (!pending.assigned || typeof pending.assigned !== "object") return false;
    const assigned = pending.assigned as Record<string, unknown>;
    if (assigned.studentId !== studentId) return false;
    if (typeof assigned.todoId !== "string" || assigned.todoId.length === 0) return false;
  }

  return true;
}

export function readPendingMetronomeRecovery(
  accountId: string,
  studentId: string,
): PendingMetronomeSave | null {
  if (typeof window === "undefined") return null;
  const key = pendingMetronomeStorageKey(accountId, studentId);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isRecoveredPendingSave(parsed, studentId)) return parsed;
    window.sessionStorage.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

function writePendingMetronomeRecovery(
  accountId: string,
  studentId: string,
  pending: PendingMetronomeSave,
): void {
  if (typeof window === "undefined") return;
  if (!isRecoveredPendingSave(pending, studentId)) return;
  try {
    window.sessionStorage.setItem(
      pendingMetronomeStorageKey(accountId, studentId),
      JSON.stringify(pending),
    );
  } catch {
    // Recovery storage is best-effort. The mounted provider still retains the
    // frozen interval and blocks a new run if browser storage is unavailable.
  }
}

function clearPendingMetronomeRecovery(accountId: string, studentId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(pendingMetronomeStorageKey(accountId, studentId));
  } catch {
    // A successful save must not become an app error because storage is blocked.
  }
}

/**
 * A failed practice save is a frozen interval that must be resolved before a
 * new metronome run can start. The live provider owns the failure while it is
 * mounted; a validated account+learner-scoped sessionStorage copy lets the same
 * exact interval survive a family learner switch or page remount without ever
 * being offered to a different account or learner.
 *
 * Recovery is intentionally user-resolvable: retry preserves exact attribution,
 * while an explicit discard lets someone escape a permanently invalid target
 * (for example, an assignment deleted after the metronome started) instead of
 * bricking Practice forever. The gate only replaces Practice surfaces; the
 * rest of the family Hub must remain usable while a failed save is recovered.
 */
export function PendingMetronomeSaveGate({
  accountId = null,
  studentId = null,
  children,
}: {
  accountId?: string | null;
  studentId?: string | null;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const metronome = useMetronome();
  const scoped = accountId !== null && studentId !== null;
  const [recovered, setRecovered] = useState<PendingMetronomeSave | null>(() =>
    scoped ? readPendingMetronomeRecovery(accountId, studentId) : null,
  );
  const [recovering, setRecovering] = useState(false);
  const [recoveryProblem, setRecoveryProblem] = useState<string | null>(null);
  const sawLivePending = useRef(false);

  useEffect(() => {
    sawLivePending.current = false;
    setRecoveryProblem(null);
    setRecovered(
      accountId !== null && studentId !== null
        ? readPendingMetronomeRecovery(accountId, studentId)
        : null,
    );
  }, [accountId, studentId]);

  useEffect(() => {
    if (accountId === null || studentId === null) return;
    if (metronome.pendingSave) {
      writePendingMetronomeRecovery(accountId, studentId, metronome.pendingSave);
      sawLivePending.current = true;
      setRecovered(null);
      return;
    }
    if (sawLivePending.current) {
      clearPendingMetronomeRecovery(accountId, studentId);
      sawLivePending.current = false;
    }
  }, [accountId, studentId, metronome.pendingSave]);

  const pending = metronome.pendingSave ?? recovered;
  if (!pending || !isPracticePath(pathname)) return <>{children}</>;

  const retry = async () => {
    if (metronome.pendingSave) {
      await metronome.retrySave();
      return;
    }
    if (!recovered || recovering) return;

    setRecovering(true);
    setRecoveryProblem(null);
    try {
      await savePendingMetronomeInterval(recovered);
      if (accountId !== null && studentId !== null) {
        clearPendingMetronomeRecovery(accountId, studentId);
      }
      setRecovered(null);
    } catch (cause: unknown) {
      if (cause instanceof ApiError && (cause.code === "practice_overlap" || cause.code === "practice_deleted")) {
        if (accountId !== null && studentId !== null) {
          clearPendingMetronomeRecovery(accountId, studentId);
        }
        setRecovered(null);
        setRecoveryProblem(null);
        return;
      }
      setRecoveryProblem(
        cause instanceof Error
          ? `The practice record is still not saved: ${cause.message}`
          : "The practice record is still not saved.",
      );
    } finally {
      setRecovering(false);
    }
  };

  const discard = () => {
    if (recovering || metronome.saving) return;
    if (metronome.pendingSave) {
      metronome.discardPendingSave();
    } else {
      setRecovered(null);
      setRecoveryProblem(null);
    }
    if (accountId !== null && studentId !== null) {
      clearPendingMetronomeRecovery(accountId, studentId);
    }
  };

  const saving = metronome.pendingSave ? metronome.saving : recovering;
  const problem = metronome.pendingSave ? metronome.problem : recoveryProblem;

  return (
    <div className="panel" role="alert">
      <h2>Save the previous metronome session</h2>
      <p>
        The metronome stopped, but that practice interval has not been saved yet.
        Retry it before starting another session so the original time, tempo and
        assignment cannot be lost.
      </p>
      {problem && <p className="error">{problem}</p>}
      <div className="actions">
        <button type="button" disabled={saving} onClick={() => void retry()}>
          {saving ? "Retrying…" : "Retry saving this session"}
        </button>
        <button type="button" className="quiet" disabled={saving} onClick={discard}>
          Discard unsaved session
        </button>
      </div>
    </div>
  );
}

export function MetronomeProvider({
  studentId,
  recoveryAccountId,
  children,
}: {
  studentId: string;
  recoveryAccountId?: string | null;
  children: ReactNode;
}) {
  const auth = useOptionalAuth();
  const accountId = recoveryAccountId ?? auth?.me?.user.id ?? null;
  const providerKey = `${accountId ?? "signed-out"}:${studentId}`;

  const persistUnmounted = (pending: PendingMetronomeSave) => {
    if (accountId !== null) {
      writePendingMetronomeRecovery(accountId, studentId, pending);
    }
  };
  const clearUnmounted = () => {
    if (accountId !== null) {
      clearPendingMetronomeRecovery(accountId, studentId);
    }
  };

  return (
    <BaseMetronomeProvider
      key={providerKey}
      studentId={studentId}
      onUnmountPendingSave={persistUnmounted}
      onUnmountPendingSaveCleared={clearUnmounted}
    >
      <PendingMetronomeSaveGate accountId={accountId} studentId={studentId}>
        {children}
      </PendingMetronomeSaveGate>
    </BaseMetronomeProvider>
  );
}
